import { useState } from 'react';
import type { FundSnapshot } from '../../core/types.ts';
import { compact, pct, shortAddr, solscanToken, toneClass, usd, usdSigned } from '../lib/format.ts';
import { useFlash } from '../lib/useSnapshot.ts';
import { Meter, Pill } from './ui.tsx';

/** Per-side theming, so the two cards are instantly distinguishable. */
export function sideTheme(side: 'long' | 'short') {
  return side === 'short'
    ? {
        text: 'text-short',
        ring: 'ring-short/50',
        border: 'border-short/30',
        glow: 'shadow-[0_0_40px_-12px_rgba(255,59,59,0.4)]',
        bar: 'bg-short',
        soft: 'bg-short/10',
        pill: 'brand' as const,
        arrow: '↓',
      }
    : {
        text: 'text-long',
        ring: 'ring-long/50',
        border: 'border-long/30',
        glow: 'shadow-[0_0_40px_-12px_rgba(47,196,107,0.4)]',
        bar: 'bg-long',
        soft: 'bg-long/10',
        pill: 'up' as const,
        arrow: '↑',
      };
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-2 py-1">
      <span className="text-[11px] text-dim">{label}</span>
      <span className="num text-[13px] font-medium">{children}</span>
    </div>
  );
}

export function FundCard({ fund }: { fund: FundSnapshot }) {
  const t = sideTheme(fund.side);
  const [copied, setCopied] = useState(false);
  const equityFlash = useFlash(fund.equityUsd);

  const deployedPct = fund.equityUsd > 0 ? fund.deployedCollateralUsd / fund.equityUsd : 0;

  async function copyMint() {
    if (!fund.token.mint) return;
    await navigator.clipboard.writeText(fund.token.mint);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  }

  return (
    <section className={`panel ${t.border} ${t.glow} overflow-hidden`}>
      <header className="flex items-center gap-3 border-b border-edge px-4 py-3">
        <img
          src={fund.logo}
          alt={`${fund.name} logo`}
          className={`size-14 shrink-0 rounded-full ring-2 ${t.ring}`}
          width={56}
          height={56}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h2 className="truncate text-base font-bold tracking-tight">{fund.name}</h2>
            <Pill tone={t.pill}>
              {t.arrow} {fund.side.toUpperCase()}
            </Pill>
          </div>
          <p className="truncate text-[11px] text-dim">{fund.tagline}</p>
        </div>
        <div className="shrink-0 text-right">
          <p className={`num text-xl leading-none font-semibold ${equityFlash}`}>{usd(fund.equityUsd)}</p>
          <p className={`num mt-0.5 text-[11px] ${toneClass(fund.unrealizedPnlUsd)}`}>
            {usdSigned(fund.unrealizedPnlUsd)} open
          </p>
        </div>
      </header>

      <div className="grid gap-x-6 gap-y-0 px-4 py-3 sm:grid-cols-2">
        <div>
          <Row label="Realized P&L">
            <span className={toneClass(fund.lifetime.realizedPnlUsd)}>{usdSigned(fund.lifetime.realizedPnlUsd)}</span>
          </Row>
          <Row label="Creator fees in">{usd(fund.lifetime.creatorFeesClaimedUsd)}</Row>
          <Row label="Borrowing costs">
            <span className={toneClass(fund.lifetime.fundingUsd)}>{usdSigned(fund.lifetime.fundingUsd)}</span>
          </Row>
          <Row label="Trading fees">
            <span className="text-down">-{usd(fund.lifetime.tradingFeesUsd).replace('$', '$')}</span>
          </Row>
        </div>
        <div>
          <Row label={`$${fund.symbol} burned`}>
            <span className="text-burn">{usd(fund.lifetime.burnedUsd)}</span>
          </Row>
          <Row label="Tokens destroyed">
            <span className="text-burn">{compact(fund.lifetime.tokensBurned)}</span>
          </Row>
          <Row label="Supply burned">
            <span className="text-burn">
              {fund.token.burnedSupplyPct === null ? '—' : `${fund.token.burnedSupplyPct.toFixed(4)}%`}
            </span>
          </Row>
          <Row label="Win rate">
            {fund.winRate === null ? (
              <span className="text-dim">no trades yet</span>
            ) : (
              <>
                {pct(fund.winRate, 0)}{' '}
                <span className="text-dim">
                  ({fund.lifetime.wins}W/{fund.lifetime.losses}L)
                </span>
              </>
            )}
          </Row>
        </div>
      </div>

      <div className="px-4 pb-3">
        <div className="mb-1 flex items-baseline justify-between text-[11px]">
          <span className="text-dim">
            Capital deployed <span className="num text-muted">{pct(deployedPct, 0)}</span>
          </span>
          <span className="num text-dim">
            {usd(fund.deployedCollateralUsd, 0)} margin · {usd(fund.freeCollateralUsd, 0)} idle
          </span>
        </div>
        <Meter value={deployedPct} tone={fund.side === 'short' ? 'down' : 'up'} />

        <div className="mt-2.5 mb-1 flex items-baseline justify-between text-[11px]">
          <span className="text-dim">
            Hard risk cap <span className="num text-muted">{pct(fund.limits.deployedPctOfCap, 0)}</span>
          </span>
          <span className="num text-dim">
            {usd(fund.deployedCollateralUsd, 0)} of {usd(fund.limits.maxDeployedUsd, 0)} max
          </span>
        </div>
        <Meter value={fund.limits.deployedPctOfCap} tone={fund.limits.atCap ? 'down' : 'brand'} />
      </div>

      <footer className="flex flex-wrap items-center gap-2 border-t border-edge px-4 py-2.5">
        {fund.pendingBurnUsd > 0 && (
          <Pill tone="burn" title="Burn credit waiting to clear the minimum swap size">
            {usd(fund.pendingBurnUsd)} queued to burn
          </Pill>
        )}
        {fund.halt && (
          <Pill tone="down" title={fund.halt.reason}>
            entries paused
          </Pill>
        )}
        {fund.limits.atCap && (
          <Pill
            tone="down"
            title={`This fund may never post more than ${usd(fund.limits.maxDeployedUsd, 0)} of margin at once. Profit still accrues; it just is not put to work.`}
          >
            at {usd(fund.limits.maxDeployedUsd, 0)} capital cap
          </Pill>
        )}
        {fund.token.mint ? (
          <>
            <button
              onClick={copyMint}
              className="num rounded-md border border-edge-bright px-2 py-1 text-[11px] text-muted transition hover:border-edge-bright hover:text-white"
              title="Copy token mint address"
            >
              {copied ? 'copied!' : shortAddr(fund.token.mint, 5)}
            </button>
            <a
              href={solscanToken(fund.token.mint)}
              target="_blank"
              rel="noreferrer"
              className="rounded-md border border-edge-bright px-2 py-1 text-[11px] text-muted transition hover:text-white"
            >
              solscan ↗
            </a>
            <a
              href={`https://pump.fun/coin/${fund.token.mint}`}
              target="_blank"
              rel="noreferrer"
              className={`rounded-md border px-2 py-1 text-[11px] font-medium ${t.border} ${t.text} ${t.soft} transition hover:brightness-125`}
            >
              buy ${fund.symbol} ↗
            </a>
          </>
        ) : (
          <Pill tone="neutral" title={`Set ${fund.id.toUpperCase()}_TOKEN_MINT once the coin is launched on pump.fun`}>
            mint not set — not launched yet
          </Pill>
        )}
      </footer>
    </section>
  );
}
